import React, { useState } from 'react';
import { Spinner, LoadingButton, useStateContext } from '@veda/dashboard-ui';
import { BsBellSlash, BsCheckAll, BsCircleFill } from 'react-icons/bs';
import { useNotifiche } from '../../ppp/hooks/useNotifiche';
import { useNotificheOperatoreApi } from '../services/notificheApi';

const TIPO_LABELS = {
  nuova_proposta:                'Proposta ricevuta',
  proposta_valutata:             'Proposta valutata',
  interesse_pubblico_dichiarato: 'Interesse pubblico dichiarato',
  interesse_pubblico_pubblicato: 'Interesse pubblico pubblicato',
  provvedimento_emesso:          'Provvedimento emesso',
  nuovo_documento:               'Nuovo documento',
  cambio_stato_procedimento:     'Aggiornamento procedimento',
};

const TIPO_COLORS = {
  provvedimento_emesso:          'text-purple-500',
  proposta_valutata:             'text-blue-500',
  interesse_pubblico_pubblicato: 'text-green-500',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function NotificaRow({ notifica, onMarcaLetta }) {
  const dotColor = TIPO_COLORS[notifica.tipo] ?? 'text-gray-400';
  return (
    <div className={`flex gap-3 p-4 border-b last:border-b-0 ${notifica.letta ? 'bg-white dark:bg-gray-800' : 'bg-blue-50 dark:bg-blue-900/10'}`}>
      <div className="pt-1 flex-shrink-0">
        <BsCircleFill size={8} className={notifica.letta ? 'text-gray-200' : dotColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className={`text-sm font-medium ${notifica.letta ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
              {notifica.titolo}
            </p>
            {notifica.procedimento_titolo && (
              <p className="text-xs text-gray-500 mt-0.5">{notifica.procedimento_titolo}</p>
            )}
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(notifica.created_at)}</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{notifica.messaggio}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {TIPO_LABELS[notifica.tipo] ?? notifica.tipo}
          </span>
          {!notifica.letta && (
            <button onClick={() => onMarcaLetta(notifica.id)} className="text-xs text-blue-600 hover:underline">
              Segna come letta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OperatoreNotifichePage() {
  const api = useNotificheOperatoreApi();
  const { addToast } = useStateContext();
  const [soloNonLette, setSoloNonLette] = useState(false);
  const [marcandoTutte, setMarcandoTutte] = useState(false);

  const { notifiche, pagination, loading, refresh } = useNotifiche({ nonLette: soloNonLette }, api);

  const handleMarcaLetta = (id) => api.marcaNotificaLetta(id, { onSuccess: () => refresh() });

  const handleMarcaTutteLette = () => {
    setMarcandoTutte(true);
    api.marcaTutteNotificheLette({
      onSuccess: () => { addToast?.({ title: 'Notifiche aggiornate', type: 'success' }); refresh(); setMarcandoTutte(false); },
      onFormError: () => setMarcandoTutte(false),
    });
  };

  const nonLetteCount = notifiche.filter((n) => !n.letta).length;

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Notifiche
          {nonLetteCount > 0 && (
            <span className="ml-2 text-sm font-normal text-blue-600">({nonLetteCount} non lette)</span>
          )}
        </h1>
        <div className="flex items-center gap-3">
          <button
            type="button" role="switch" aria-checked={soloNonLette}
            onClick={() => setSoloNonLette((v) => !v)}
            className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-300 select-none focus:outline-none"
          >
            Solo non lette
            <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${soloNonLette ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${soloNonLette ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </span>
          </button>
          <span className={nonLetteCount === 0 ? 'opacity-50 pointer-events-none' : ''}>
            <LoadingButton isLoading={marcandoTutte} customFunc={handleMarcaTutteLette} icon={<BsCheckAll size={16} />} text="Segna tutte lette" />
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : notifiche.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border rounded-lg shadow-sm">
          <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
            <BsBellSlash size={36} />
            <p className="text-sm">{soloNonLette ? 'Nessuna notifica non letta.' : 'Nessuna notifica.'}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border rounded-lg shadow-sm overflow-hidden">
          {notifiche.map((n) => <NotificaRow key={n.id} notifica={n} onMarcaLetta={handleMarcaLetta} />)}
        </div>
      )}

      {pagination && pagination.lastPage > 1 && (
        <p className="text-xs text-gray-500 text-center mt-4">
          Pagina {pagination.currentPage} di {pagination.lastPage} — {pagination.total} notifiche totali
        </p>
      )}
    </div>
  );
}
